/**
 * Renders the "Output" section (section 2) of ProcessTaskSchemaEditorContent,
 * including output prefix, autopopulate toggle, output field table, task finder,
 * and the add/remove output schema button. Single-use component extracted from
 * ProcessTaskSchemaEditorContent to reduce its complexity.
 */
import {css} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import FormGroup from 'ui-library/bs/FormGroup';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import ErrorList from 'ui-library/spec-editor/ErrorList';
import copyToClipboard from 'util/clipboard/copyToClipboard';

import {
  getOutputPrefixFieldProps,
  OutputAutopopulateField,
  OutputTaskFinderSection,
} from './OutputConfigFields';
import {ProcessSchemaFieldTable} from './ProcessSchemaFieldComponents';
import type {ResultSchemasGQL, StorableSchemaGQL} from './ProcessSchemaFieldComponents';
import ProcessSchemaMetadataSectionRow, {PrefixField} from './ProcessSchemaMetadataSectionRow';
import {EXECUTION_TYPES_WITH_TASK_FINDER} from './ProcessTaskSchemaEditorConstants';
import ProcessTaskSchemaEditorSection from './ProcessTaskSchemaEditorSection';
import {styles} from './ProcessTaskSchemaEditorStyles';
import type {ProcessTaskSchema} from './ProcessTaskSchemaUtils';
import type {ProcessTaskSchemaEditorStateResult} from './useProcessTaskSchemaEditorState';

interface OutputConfigSectionProps {
  state: ProcessTaskSchemaEditorStateResult;
  processTaskSchema: ProcessTaskSchema | null;
  storableSchemas: Array<StorableSchemaGQL> | null;
  resultSchemas: Array<ResultSchemasGQL> | null;
  registryId?: string;
  isSettingsPage?: boolean;
}

function ToggleOutputSchemaButton(props: {hasOutputSchema: boolean; onClick: () => void}): JSX.Element {
  return (
    <div className={css(styles.outputSchemaToggle)}>
      <FormGroup className={classNames('u-no-spacing-bottom', css(styles.descriptionContents))}>
        <div className="settingsPageContainer-label">
          <Button
            buttonStyle={ButtonStyle.LINK}
            onClick={props.onClick}
            className={props.hasOutputSchema ? css(styles.removeText) : ''}
            dataTestAttrs={{
              component: 'ProcessTaskSchemaEditor',
              element: 'outputSchemaControlButton',
              key: props.hasOutputSchema ? 'remove' : 'add',
            }}
          >
            {props.hasOutputSchema ? 'Remove output schema' : 'Add output schema'}
          </Button>
        </div>
        <div className={css(styles.descriptionText)}>
          {`Output schema cannot be ${props.hasOutputSchema ? 'removed' : 'added'} after the task schema is
          created.`}
        </div>
      </FormGroup>
    </div>
  );
}

export default function OutputConfigSection(props: OutputConfigSectionProps): JSX.Element {
  const {state, processTaskSchema} = props;

  return (
    <ProcessTaskSchemaEditorSection
      isLastSection={!state.canConfigureFlowchart}
      sectionTitle="Output"
      sectionNumber={2}
      actionButton={
        !state.isBeingCreated && processTaskSchema?.outputSchema?.id
          ? {
              onClick: () => {
                copyToClipboard(processTaskSchema.outputSchema!.id, {
                  showToastOnSuccess: true,
                  toastText: 'Copied output schema API ID to clipboard',
                });
              },
              tooltipTitle: 'Copy API ID',
              icon: 'note-code',
            }
          : undefined
      }
      shouldHideSectionHeader={state.shouldHideSectionHeaders}
    >
      {state.hasOutputSchema && (
        <>
          <ProcessSchemaMetadataSectionRow>
            <PrefixField {...getOutputPrefixFieldProps(state, 'ID prefix')} />
          </ProcessSchemaMetadataSectionRow>
          {state.executionType === ProcessExecutionType.DIRECT && (
            <ProcessSchemaMetadataSectionRow>
              <OutputAutopopulateField state={state} />
            </ProcessSchemaMetadataSectionRow>
          )}
          <ErrorList
            dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'outputSchemaErrorList'}}
            errorMessage={state.errors.outputFields}
          />
          <ProcessSchemaFieldTable
            isDisabled={state.isArchived || state.isEditingFlowchart || state.isLocked}
            key={state.outputFieldTableKey}
            hasBottomLink={state.hasOutputSchema}
            schema={processTaskSchema?.outputSchema ?? null}
            ownerId={state.schemaOwner!.id}
            ownerUrlId={state.schemaOwner!.url_identifier}
            registryId={props.registryId}
            onFieldsChange={(updatedOutputFields) => {
              state.onFieldsChanged(
                state.outputFields,
                updatedOutputFields,
                'outputFields',
                state.setOutputFields
              );
            }}
            schemaType="output"
            storableSchemas={props.storableSchemas}
            fields={state.outputFields}
            resultSchemas={props.resultSchemas}
            dataTestAttrs={{component: 'ProcessTaskSchemaEditor', element: 'outputTable'}}
            compactMode={props.isSettingsPage === false}
          />
          {EXECUTION_TYPES_WITH_TASK_FINDER.has(state.executionType) && (
            <OutputTaskFinderSection state={state} processTaskSchema={processTaskSchema} />
          )}
        </>
      )}
      {state.isBeingCreated && (
        <ToggleOutputSchemaButton
          hasOutputSchema={state.hasOutputSchema}
          onClick={state.toggleHasOutputSchemaButton}
        />
      )}
      {!state.hasOutputSchema && !state.isBeingCreated && (
        <div className={css(styles.descriptionText)}>
          Output schema cannot be added after the task schema is created.
        </div>
      )}
    </ProcessTaskSchemaEditorSection>
  );
}
